import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

function ProductCard({ product, onAddToCart, onCardClick }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = product.images && product.images.length > 0 
    ? product.images 
    : [product.primary_image || '/placeholder-image.png'];

  function handlePrevImage(e) {
    e.stopPropagation();
    setCurrentImageIndex(prev => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  }

  function handleNextImage(e) {
    e.stopPropagation();
    setCurrentImageIndex(prev => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  }

  function handleAddToCart(e) {
    e.stopPropagation();
    onAddToCart({
      ...product,
      seller: product.seller  // Ensure seller info is included
    }, 1);  // Keep the quantity as 1
  }

  function handleCardClick() {
    onCardClick(product);
  }

  return (
    <div 
      onClick={handleCardClick}
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
    >
      {/* Image Carousel */}
      <div className="relative aspect-square bg-slate-100">
        <img
          src={images[currentImageIndex]}
          alt={product.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = '/placeholder-image.png';
          }}
        />

        {/* Carousel Controls - Only show if multiple images */}
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
              aria-label="Imagen anterior"
            >
              <ChevronLeft className="w-4 h-4 text-slate-700" />
            </button>

            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
              aria-label="Siguiente imagen"
            >
              <ChevronRight className="w-4 h-4 text-slate-700" />
            </button>

            {/* Image Indicators */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    index === currentImageIndex
                      ? 'bg-brand-primary w-4'
                      : 'bg-white/60'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Seller Badge */}
        <div className="absolute top-2 left-2 bg-white/95 px-2 py-1 rounded-full shadow-sm">
          <p className="text-xs font-semibold text-slate-700 font-montserrat">
            {product.seller.username}
          </p>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4">
        <h3 className="font-bold text-slate-800 font-montserrat mb-2 line-clamp-2 min-h-[3rem]">
          {product.title}
        </h3>

        {product.description && (
          <p className="text-xs text-slate-500 font-montserrat mb-3 line-clamp-2">
            {product.description}
          </p>
        )}

        {/* Pricing */}
        <div className="space-y-1.5 mb-4">
          {product.bulk_price > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600 font-montserrat">Precio Mayorista:</span>
              <span className="text-sm font-bold text-brand-gold font-montserrat">
                S/ {product.bulk_price.toFixed(2)}
              </span>
            </div>
          )}
          
          {product.dropship_price > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600 font-montserrat">Precio Dropship:</span>
              <span className="text-sm font-bold text-brand-green font-montserrat">
                S/ {product.dropship_price.toFixed(2)}
              </span>
            </div>
          )}

          {!product.bulk_price && !product.dropship_price && product.price > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600 font-montserrat">Precio:</span>
              <span className="text-sm font-bold text-brand-primary font-montserrat">
                S/ {product.price.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <button
          onClick={handleAddToCart}
          className="w-full flex items-center justify-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white py-2.5 rounded-lg transition-all font-montserrat text-sm font-semibold shadow-sm hover:shadow-md"
        >
          <Plus className="w-4 h-4" />
          <span>Agregar al Carrito</span>
        </button>
      </div>
    </div>
  );
}

export default ProductCard;